// Customer-facing quote viewer at /q/:token. Public route — no auth.
// Shows the BS-compliant quote summary, captures a digital signature
// acceptance, then redirects to Stripe Checkout for the deposit and
// polls the backend until payment status settles.
import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { ShieldCheck, CheckCircle, CreditCard, Warning, House, FilePdf } from "@phosphor-icons/react";

// Use the same-origin URL the page is served from. The platform routes
// /api/* to the FastAPI backend automatically, so this works across all
// deployment environments (preview, production, custom domains) without
// baking REACT_APP_BACKEND_URL into the bundle at build time.
const API = typeof window !== "undefined" ? window.location.origin : "";
const POUND = (n) => `£${(Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function SignaturePad({ value, onChange }) {
  const ref = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1e293b";
  }, []);

  const getPos = (e) => {
    const c = ref.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return {
      x: ((t ? t.clientX : e.clientX) - rect.left),
      y: ((t ? t.clientY : e.clientY) - rect.top),
    };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const { x, y } = getPos(e);
    const ctx = ref.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = ref.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    onChange?.(ref.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c = ref.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
    onChange?.(null);
  };

  return (
    <div>
      <div className="border border-slate-300 bg-white rounded-sm overflow-hidden" style={{ height: 140 }}>
        <canvas
          ref={ref}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          className="w-full h-full block cursor-crosshair touch-none"
          data-testid="signature-canvas"
        />
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] text-slate-500 uppercase tracking-wider">{hasDrawn ? "Signature captured" : "Sign here with mouse or finger"}</span>
        <button type="button" onClick={clear} className="text-[10px] uppercase tracking-wider font-bold text-slate-500 hover:text-red-600" data-testid="signature-clear">Clear</button>
      </div>
    </div>
  );
}

export default function PublicQuoteViewer() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [sig, setSig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [polling, setPolling] = useState(false);
  const polledOnce = useRef(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/public/quotes/${token}`);
      setQuote(data);
    } catch (e) {
      setError(e.response?.data?.detail || "This quote link is invalid or has expired.");
    }
  };

  useEffect(() => { load(); }, [token]);

  // After Stripe redirect — poll status
  useEffect(() => {
    const sid = params.get("session_id");
    const dep = params.get("deposit");
    if (!sid || polledOnce.current) return;
    polledOnce.current = true;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/quotes/${token}/payment-status?session_id=${encodeURIComponent(sid)}`);
        if (data.payment_status === "paid") {
          toast.success("Deposit paid — thank you!");
          await load();
          setPolling(false);
          params.delete("session_id"); params.delete("deposit"); setParams(params, { replace: true });
          return;
        }
        if (data.status === "expired") {
          toast.error("Payment session expired — please try again.");
          setPolling(false);
          return;
        }
        attempts += 1;
        if (attempts < 8) setTimeout(tick, 2000);
        else { setPolling(false); toast.info("Payment is still processing — refresh in a minute."); }
      } catch (e) {
        setPolling(false);
      }
    };
    if (dep === "cancelled") { toast.info("Payment cancelled — you can try again at any time."); }
    else tick();
  }, [params.get("session_id")]);

  const acceptAndPay = async () => {
    if (!agree) { toast.error("Tick the acceptance box to continue."); return; }
    if (!name.trim()) { toast.error("Type your full name to sign."); return; }
    setSubmitting(true);
    try {
      // 1) Accept (writes signature, flips status to Accepted)
      const { data: acc } = await axios.post(`${API}/api/public/quotes/${token}/accept`, {
        accepted: true,
        accepted_name: name.trim(),
        signature_data_url: sig,
      });
      setQuote(acc);
      // 2) Create Stripe checkout if deposit > 0 and not already paid
      if ((acc.deposit?.amount || 0) > 0 && acc.deposit?.status !== "paid") {
        const { data: co } = await axios.post(`${API}/api/public/quotes/${token}/checkout`, {
          origin_url: window.location.origin,
        });
        window.location.href = co.url;
        return;
      }
      toast.success("Quote accepted — thank you!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not complete acceptance.");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <Toaster position="top-right" richColors closeButton />
        <div className="card-elev p-8 max-w-md text-center">
          <Warning size={32} weight="fill" className="text-amber-600 mx-auto" />
          <h1 className="font-display text-2xl font-bold mt-3">Quote not available</h1>
          <p className="text-sm text-slate-500 mt-2">{error}</p>
        </div>
      </div>
    );
  }
  if (!quote) {
    return <div className="min-h-screen grid place-items-center text-slate-400">Loading…</div>;
  }

  const sp = quote.summary_public || {};
  const dep = quote.deposit || {};
  const accepted = quote.acceptance?.accepted_at;
  const depositPaid = dep.status === "paid";

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors closeButton />

      {/* Brand header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <House size={24} weight="fill" className="text-[var(--rqp-orange)]" />
            <div>
              <div className="font-display font-bold text-xl tracking-tight">{quote.contractor?.name || "RoofQuote Pro"}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Professional roofing specification</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Quote</div>
            <div className="font-mono-data text-sm font-bold">{quote.quote_number}</div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6">

        {/* Status banner */}
        {(accepted || depositPaid) && (
          <div className={`card-elev p-4 flex items-center gap-3 ${depositPaid ? "bg-emerald-50 border-emerald-200" : "bg-blue-50 border-blue-200"}`} data-testid="status-banner">
            <CheckCircle size={20} weight="fill" className={depositPaid ? "text-emerald-600" : "text-blue-600"} />
            <div>
              <div className="font-display font-bold text-sm">
                {depositPaid ? "Quote accepted and deposit paid" : "Quote accepted"}
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5">
                Accepted by <span className="font-semibold">{quote.acceptance?.accepted_name}</span>
                {accepted ? ` · ${new Date(accepted).toLocaleString("en-GB")}` : ""}
                {depositPaid && dep.paid_at ? ` · Deposit £${dep.amount} paid on ${new Date(dep.paid_at).toLocaleDateString("en-GB")}` : ""}
              </div>
            </div>
          </div>
        )}

        {/* Quote header */}
        <section className="card-elev p-6">
          <div className="overline">Quote for</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">{quote.title}</h1>
          <div className="text-sm text-slate-500 mt-1">{quote.customer_name}</div>
          {quote.site_address && (
            <div className="text-xs text-slate-500 mt-1 whitespace-pre-line"><strong>Site:</strong> {quote.site_address}</div>
          )}
        </section>

        {/* Total */}
        <section className="card-elev p-6">
          <div className="overline">Investment</div>
          <div className="flex items-baseline gap-3 mt-1">
            <div className="font-mono-data text-5xl font-bold text-[var(--rqp-orange)]" data-testid="quote-total">{POUND(sp.total)}</div>
            <div className="text-xs uppercase tracking-wider text-slate-500">{sp.vat_enabled === false ? "(no VAT)" : `inc. ${sp.vat_pct}% VAT`}</div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm mt-3 font-mono-data">
            <div className="flex justify-between"><span className="text-slate-500 font-sans">Subtotal</span><span>{POUND(sp.subtotal)}</span></div>
            {sp.vat_enabled !== false && <div className="flex justify-between"><span className="text-slate-500 font-sans">VAT ({sp.vat_pct}%)</span><span>{POUND(sp.vat)}</span></div>}
          </div>
        </section>

        {/* Sections breakdown */}
        {quote.sections?.length > 0 && (
          <section className="card-elev p-6">
            <div className="overline">Roof sections</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              {quote.sections.map((s, i) => {
                const ps = quote.calculations?.per_section?.[i] || {};
                return (
                  <div key={s.id || `section-${i}`} className="border border-slate-200 p-3 rounded-sm">
                    <div className="font-display font-bold text-sm">{s.name || `Section ${i + 1}`}</div>
                    <div className="text-[11px] text-slate-500 font-mono-data mt-1">
                      {Number(s.length || 0).toFixed(2)}m × {Number(s.eave_to_ridge || 0).toFixed(2)}m E2R · {s.pitch}° · {s.slope_coverage}
                    </div>
                    <div className="text-xs mt-2 font-mono-data">
                      Area: <span className="font-bold">{ps.roof_area_m2?.toFixed(1)} m²</span> · Battens: {ps.battens_m?.toFixed(0)} m · Ridge: {ps.ridge_length_m?.toFixed(1)} m
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* BS spec preview */}
        {quote.specifications?.per_section?.some((p) => p.covering_resolved) && (
          <section className="card-elev p-6">
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} weight="fill" className="text-[var(--rqp-blue)]" />
              <h2 className="font-display font-bold text-lg">BS 5534 / BS 5250 Specification</h2>
            </div>
            <p className="text-xs text-slate-500 mt-1">Compliant with British Standards for slating, tiling and ventilation.</p>
            <div className="mt-3 space-y-2 text-xs font-mono-data">
              {quote.specifications.per_section.map((p, i) => {
                if (!p.covering_resolved) return null;
                const secKey = quote.sections?.[i]?.id || `spec-${i}`;
                return (
                  <div key={secKey} className="border border-slate-200 p-3 rounded-sm">
                    <div className="font-display font-bold text-sm">{quote.sections?.[i]?.name || `Section ${i + 1}`}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{p.covering_resolved.name || p.covering_resolved.category} · {p.covering_resolved.ref}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                      <div><div className="text-[10px] text-slate-500">Batten gauge</div><div className="font-bold">{p.batten_gauge_mm || "—"} mm</div></div>
                      <div><div className="text-[10px] text-slate-500">Headlap</div><div className="font-bold">{p.headlap_mm || "—"} mm</div></div>
                      <div><div className="text-[10px] text-slate-500">Tiles</div><div className="font-bold">{p.tile_quantity || "—"}</div></div>
                      <div><div className="text-[10px] text-slate-500">Weight</div><div className="font-bold">{p.weight_kg_per_m2 || "—"} kg/m²</div></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Materials */}
        {quote.materials?.length > 0 && (
          <section className="card-elev p-6">
            <div className="overline">Materials</div>
            <table className="w-full text-sm mt-2">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-500">
                  <th className="py-1 font-medium">Item</th>
                  <th className="py-1 font-medium text-right">Qty</th>
                  <th className="py-1 font-medium">Unit</th>
                </tr>
              </thead>
              <tbody className="font-mono-data">
                {quote.materials.map((m, i) => (
                  <tr key={m._uid || m.material_id || `material-${i}-${m.name || ""}`} className="border-b border-slate-100">
                    <td className="py-1 font-sans">{m.name}<div className="text-[10px] uppercase tracking-wider text-slate-500">{m.manufacturer}</div></td>
                    <td className="py-1 text-right">{Number(m.quantity).toFixed(1)}</td>
                    <td className="py-1 text-xs">/ {m.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* PDF download */}
        <section className="card-elev p-4 flex items-center justify-between gap-3">
          <div className="text-xs text-slate-600">Need a copy? Download the full specification pack.</div>
          <a
            href={`${API}/api/public/quotes/${token}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="btn-secondary flex items-center gap-2"
            data-testid="public-pdf"
          >
            <FilePdf size={14} weight="bold" /> Download PDF
          </a>
        </section>

        {/* Acceptance & deposit */}
        {!accepted && !depositPaid && (
          <section className="card-elev p-6" data-testid="acceptance-section">
            <div className="overline">Accept this quote</div>
            <h2 className="font-display text-xl font-bold mt-1">Sign &amp; pay deposit</h2>
            <p className="text-sm text-slate-500 mt-1">
              By accepting, you agree to the works described above. A deposit of <strong className="font-mono-data">{POUND(dep.amount)}</strong> ({dep.pct}% of total) secures your booking. Balance due on completion.
            </p>

            <div className="grid grid-cols-1 gap-4 mt-5">
              <label className="block">
                <span className="overline">Your full name (digital signature)</span>
                <input
                  className="rqp mt-1.5"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Jane Smith"
                  data-testid="accept-name"
                />
              </label>
              <div>
                <span className="overline">Signature (optional)</span>
                <div className="mt-1.5"><SignaturePad value={sig} onChange={setSig} /></div>
              </div>
              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[var(--rqp-orange)]"
                  data-testid="accept-checkbox"
                />
                <span className="text-sm">
                  I confirm I have authority to accept this quote and agree to the works, prices and BS 5534 / BS 5250 compliance specification above. I understand the {dep.pct}% deposit is non-refundable once works commence.
                </span>
              </label>
            </div>

            <button
              type="button"
              onClick={acceptAndPay}
              disabled={submitting || !agree || !name.trim()}
              className="btn-primary mt-5 w-full sm:w-auto flex items-center gap-2 justify-center"
              data-testid="accept-and-pay"
            >
              <CreditCard size={14} weight="fill" />
              {submitting ? "Processing…" : (dep.amount > 0 ? `Accept & Pay ${POUND(dep.amount)} Deposit` : "Accept Quote")}
            </button>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-3">
              Secured by Stripe. We never see or store your card details.
            </div>
          </section>
        )}

        {/* Polling banner */}
        {polling && (
          <section className="card-elev p-4 bg-blue-50 border-blue-200 text-blue-800 text-sm" data-testid="polling-banner">
            Verifying payment — please don&apos;t close this window…
          </section>
        )}

        <footer className="text-center text-[10px] uppercase tracking-[0.18em] text-slate-500 py-6">
          Powered by RoofQuote Pro · BS 5534 / BS 5250 compliant specification
        </footer>
      </main>
    </div>
  );
}
